import "breadcrumbs-pattern/dist/main.css";
import React from "react";
import { Link } from "react-router-dom";
import chevronLeftSmall from "../../../../public/assets/images/chevron-left-small-black.svg";
import chevronRightSmall from "../../../../public/assets/images/chevron-right-small-black.svg";
import { BreadcrumbsProps, BreadcrumbItem } from "./types";
import './BreadCrumbs.css';

/**
 * Reusable BreadCrumbs component
 * Supports both legacy props (previousPage, currentPage) and new items array
 * 
 * @example
 * ```tsx
 * // Legacy usage (2 levels)
 * <BreadCrumbs previousPage="Home" currentPage="Details" onPreviousClick={() => {}} />
 * 
 * // New usage (multiple levels with links)
 * <BreadCrumbs items={[
 *   { label: "Home", onClick: () => navigate(-1) },
 *   { label: "Projects", path: "/projects/123" },
 *   { label: "Details" }
 * ]} />
 * ```
 */
const BreadCrumbs = ({
  items,
  previousPage,
  currentPage,
  onPreviousClick,
  stackOnMobile = false,
}: BreadcrumbsProps) => {
  // Legacy behavior check for backward compatibility
  const hasCurrent = !!(currentPage && String(currentPage).trim().length > 0);

  /**
   * Render a single breadcrumb item
   */
  const renderBreadcrumbItem = (item: BreadcrumbItem, index: number, isLast: boolean) => {
    let content: React.ReactNode;

    // Render as link if path is provided
    if (item.path) {
      content = (
        <Link
          to={item.path}
          state={item.state}
          data-analytics-type="link"
          className="underline! text-kp-blue-dark! break-all overflow-hidden text-ellipsis whitespace-nowrap max-w-60"
          title={item.title}
        >
          {item.label}
        </Link>
      );
    } 
    // Render as button if onClick handler is provided
    else if (item.onClick) {
      content = (
        <button
          type="button"
          onClick={item.onClick}
          className="underline! text-kp-blue-dark!"
          title={item.title}
        >
          {item.label}
        </button>
      );
    } 
    // Render as plain text for current page
    else {
      content = (
        <span className={isLast ? "" : "text-kp-blue-dark"} title={item.title}>
          {item.label}
        </span>
      );
    }

    return (
      <li key={index} className="inline-flex items-center">
        {content}
      </li>
    );
  };

  /**
   * Render separator between breadcrumb items
   */
  const renderSeparator = (index: number) => (
    <li key={`sep-${index}`} className="inline-flex items-center text-[#0D1C3D]">
        <img
                    aria-hidden="true"
                    className={`ds-breadcrumbs__chevron-forward inline-block align-middle w-4 h-4 filter invert-0 text-[#0D1C3D] relative! sm:top-0! ${stackOnMobile ? 'mobile:hidden! tablet:inline-block! desktop:inline-block!' : ''}`}
                    src={chevronRightSmall}
                    alt=""
                    height="16px"
                    width="16px"
                  />
    </li>
  );

  // If using new items array format, render multiple breadcrumbs
  if (items && items.length > 0) {
    return (
      <div className="mobile-margin lg:mt-15!">
        <nav
          aria-label="Breadcrumbs"
          className="ds-breadcrumbs ds-foundation text-[13px]! desktop:text-[14px]!"
          data-ds-theme="vessel"
          data-ds-variant="basic"
          data-ds-version="2"
        >
          <ul className="ds-breadcrumbs__list flex items-center gap-1 whitespace-nowrap overflow-x-auto md:flex-wrap md:whitespace-normal">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              return (
                <React.Fragment key={index}>
                  {renderBreadcrumbItem(item, index, isLast)}
                  {!isLast && renderSeparator(index)}
                </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </div>
    );
  }

  // Legacy rendering mode (backward compatibility)
  return (
    <div className="mobile-margin lg:mt-15! "> 
      <nav
        aria-label="Breadcrumbs"
        className="ds-breadcrumbs ds-foundation text-[13px]! desktop:text-[14px]!"
        data-ds-theme="vessel"
        data-ds-variant="basic"
        data-ds-version="2"
        data-ref="0"
      >
        <ul className={`ds-breadcrumbs__list flex items-center gap-1 whitespace-nowrap overflow-x-auto md:flex-wrap md:whitespace-normal ${stackOnMobile ? 'mobile:flex-col! mobile:items-start! mobile:gap-0! tablet:flex-row! tablet:items-center! desktop:flex-row! desktop:items-center!' : ''}`}>
          {hasCurrent ? (
            <>
              <li className={`inline-flex items-center ${stackOnMobile ? 'mobile:w-full! tablet:w-auto! desktop:w-auto!' : ''}`}>
                <button
                  type="button"
                  onClick={() => {
                    onPreviousClick?.();
                  }}
                  className={`underline!  text-kp-blue-dark!  ${stackOnMobile ? 'mobile:flex! mobile:flex-col! mobile:items-start! tablet:inline! desktop:inline!' : ''}`}
                >
                  <span className={stackOnMobile ? 'mobile:mb-1! tablet:mb-0! desktop:mb-0!' : ''}>{previousPage}</span>
                  <img
                    aria-hidden="true"
                    className={`ds-breadcrumbs__chevron-forward inline-block align-middle w-4 h-4 filter invert-0 text-[#0D1C3D] relative! sm:top-0! ${stackOnMobile ? 'mobile:hidden! tablet:inline-block! desktop:inline-block!' : ''}`}
                    src={chevronRightSmall}
                    alt=""
                    height="16px"
                    width="16px"
                  />
                </button>
              </li>
              <li className={`inline-flex items-center ${stackOnMobile ? 'mobile:flex! mobile:items-start! mobile:gap-2! tablet:inline-flex! tablet:items-center! desktop:inline-flex! desktop:items-center!' : ''}`}>
                <div className={stackOnMobile ? 'mobile:flex! mobile:items-start! mobile:gap-2! tablet:inline! desktop:inline!' : ''}>
                  {stackOnMobile && (
                    <img
                      aria-hidden="true"
                      className="mobile:inline-block! mobile:mt-0.5! tablet:hidden! desktop:hidden!"
                      src={chevronRightSmall}
                      alt=""
                      height="16px"
                      width="16px"
                    />
                  )}
                  {!stackOnMobile && (
                    <img
                      aria-hidden="true"
                      className="mobile-only ds-breadcrumbs__chevron-back"
                      src={chevronRightSmall}
                      alt=""
                      height="16px"
                      width="16px"
                    />
                  )}
                  {currentPage}
                </div>
              </li>
            </>
          ) : (
            <>
              <li className="inline-flex items-center w-full sm:inline-flex sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    onPreviousClick?.();
                  }}
                  className="underline! text-kp-blue-dark!  block w-full sm:inline-block sm:w-auto"
                >
                  <img
                    aria-hidden="true"
                    className="ds-breadcrumbs_chevron-back inline-block"
                    src={chevronLeftSmall}
                    alt="chevron left"
                    height="16px"
                    width="16px"
                  />
                  {previousPage}
                </button>
              </li>
              <li className="inline-flex items-center">{currentPage}</li>
            </>
          )}
        </ul>
      </nav>
    </div>
  );
};

export default BreadCrumbs;
